import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JSConfetti from 'js-confetti';
import { useQuery } from 'react-query';
import { isString } from 'radash';
import { expressVersionRetrieve } from '@goldcast/api/content';
import { NavigationEndpointConfig } from './constants';
import { getNavItems } from './utils';
import SessionDetailLayout from './SessionDetailLayout';
import { AI_GENERATED_CLIPS_LIMIT } from '../TranscriptPage/constants';
import { NavItemType } from './components/NavItem';
import { NavigationEndpoint } from './types';
import RecommendChromiumBrowserPopup from './RecommendChromiumBrowserPopup';
import EventMigrationPopup from './EventMigrationPopup';
import { core, initContent, USER_NOT_ADMIN_OR_IN_ORG_ERROR_MESSAGE } from '@/stores/core';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { TranscriptContextProvider } from '@/context/TranscriptContext/TranscriptContext';
import { PostAutoGenerateProvider } from '@/context/PostGenerationContext/PostGenerationContext';
import { useAppContext } from '@/context/AppContext/AppContext';
import useAIAddon from '@/hooks/useAIAddon';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';
import { ClipType, loadClips } from '@/libs/clipContentUtil';
import { clearAllClips, overwriteAllClips, setCanGenerateClips, isSimplifiedUploadEnabled } from '@/stores/clip';
import useContentUpload from '@/hooks/useContentUpload';
import { ContentStatesEnum } from '@/Pages/VideoImportPage/constants';
import { initUnseenClipIds, unseenClipIds } from '@/stores/unseenClips';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import useAnalytics from '@/hooks/useAnalytics';
import { handleConfettiState, removeContentUploadEntry } from '@/stores/contentUploadStore';
import { READINESS_SHOW_STATUS_EVENT } from '@/components/organisms/ContentReadinessPopup/constants';
import { currentUser } from '@/stores/user';
import { getEnvConfig } from '@/constants';
import { showWarningToast } from '@/libs/toast/toast';

function SessionDetail() {
  const [loading, setLoading] = useState(true);
  const [isAllClipsLoaded, setIsAllClipsLoaded] = useState(false);
  const [isRecordedContent, setIsRecordedContent] = useState(false);
  const featureFlagSnap = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  // Simplified-upload DONE content enters the editor without going through the legacy ContentStatesEnum
  // machine, so `contentState` never reaches ContentReadyForPreview and the clips fetch below would stay
  // disabled. Track readiness explicitly so `showSessionDetailsWithContext` (which gates the clips query)
  // is satisfied for this flow.
  const [isSimplifiedUploadReady, setIsSimplifiedUploadReady] = useState(false);
  const [shouldNavigateToImportPage, setShouldNavigateToImportPage] = useState(false);
  const unseenClipsStore = useSyncExternalStore(unseenClipIds.subscribe, unseenClipIds.getSnapshot);
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const { logger } = useAppContext();
  const hasAIAddon = useAIAddon();
  const { getContentUploadById, contentUploadList, initUploadedContent } = useContentUpload();
  const contentLoaded = useRef(false);
  const navigate = useNavigate();
  const contentUpload = getContentUploadById(broadcastId);
  const { contentState } = contentUpload;
  const { trackFetchedClips } = useAnalytics();
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const useTextGrouping = featureFlags[FeatureFlagKeys.Use_CL_Text_Assets_Grouping];
  // Read off the subscribed snapshot rather than the isHighlightReelEnabled() helper: this is render
  // state, so it has to re-run when the flag lands, and only the subscription guarantees that.
  // Highlight reels are LLM-generated, so they are gated on the AI add-on like text assets.
  const highlightReelEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Highlight_Reel] && hasAIAddon;
  const sessionDetailMountTime = useRef(performance.now());

  function getClips(clipType: ClipType) {
    return loadClips(broadcastId!, { clipTypes: clipType });
  }

  const showSessionDetailsWithContext = useMemo(() => {
    return (
      (contentState === ContentStatesEnum.ContentReadyForPreview || isRecordedContent || isSimplifiedUploadReady) &&
      contentLoaded.current &&
      !loading
    );
  }, [contentState, loading, isRecordedContent, isSimplifiedUploadReady]);

  const { refetch: refetchVideoClips } = useQuery({
    queryKey: ['clips', broadcastId],
    queryFn: () => getClips(ClipType.Clip),
    initialData: [],
    enabled: showSessionDetailsWithContext,
    onSuccess: clips => {
      trackFetchedClips({ count: (clips || []).length });
      if (clips.filter(clip => clip.asset_metadata.source === 'AI').length >= AI_GENERATED_CLIPS_LIMIT) {
        setCanGenerateClips(false);
      }
      overwriteAllClips(clips, ClipType.Clip);
      !isAllClipsLoaded && setIsAllClipsLoaded(true);
      initUnseenClipIds(clips);
    }
  });

  // Prefetch highlight reels in parallel with clips so the popover shows data immediately on open.
  // Reels land in the same store as clips so that the editor's id-matched write-backs (renames,
  // metadata saves, deletes) reach them too; consumers scope reads with useAllVisibleReels.
  // No initialData: an empty array would stamp dataUpdatedAt at creation, making the query look
  // settled-and-empty before it has run. Consumers read reels from the store, not from `data`.
  const { refetch: refetchHighlightReels } = useQuery({
    queryKey: ['highlight-reels', broadcastId],
    queryFn: () => getClips(ClipType.HighlightReel),
    enabled: showSessionDetailsWithContext && highlightReelEnabled,
    onSuccess: reels => {
      overwriteAllClips(reels, ClipType.HighlightReel);
    }
  });

  useEffect(() => {
    setIsSimplifiedUploadReady(false);
    contentLoaded.current = false;
  }, [broadcastId]);

  // The upload routing decision below reads isSimplifiedUploadEnabled(), which is only meaningful once
  // feature flags have hydrated. Standalone content-studio guarantees this via App.tsx's areFlagsLoaded
  // gate, but the embedded CL editor MF (renderContentLabEditor) deliberately does NOT mount App.tsx — so
  // on a page reload the effect could run before flags load, read a false-negative flag value, and bounce a
  // DONE-but-still-generating simplified upload to the legacy /import page. Defer until flags are loaded.
  const areFeatureFlagsLoaded = featureFlagSnap.areFlagsLoaded;

  useEffect(() => {
    const userEmail = currentUser.getSnapshot()?.email;
    const isInternalEmail = userEmail?.endsWith('@goldcast.io') || userEmail?.endsWith('@cvent.com');
    const clPlayerConfigHash = getEnvConfig('PLAYER_CONFIG_HASH');
    if (isInternalEmail && isString(clPlayerConfigHash)) {
      expressVersionRetrieve({}).then(versionInfo => {
        const expressPlayerConfigHash = (versionInfo as any).player_config_hash;
        if (expressPlayerConfigHash !== getEnvConfig('PLAYER_CONFIG_HASH')) {
          showWarningToast(
            `Mismatch in express and frontned player_config_hash ${expressPlayerConfigHash}!==${clPlayerConfigHash}`
          );
        }
      });
    }
    if (!isString(clPlayerConfigHash)) {
      logger.warn('Content Lab Env has PLAYER_CONFIG_HASH as undefined');
    }
  }, []);

  useEffect(() => {
    if (!areFeatureFlagsLoaded) {
      return;
    }
    if (broadcastId && !contentLoaded.current) {
      contentLoaded.current = true;
      initContent(broadcastId)
        .then(res => {
          const { mediaContent } = res;
          if (mediaContent.media_source_type !== 'UPLOAD') {
            setLoading(false);
            setIsRecordedContent(true);
            return;
          }
          const contentDetails = initUploadedContent(res);

          // New flow: readiness is driven by the single `upload_editor_status` signal (spec §3.3),
          // not the legacy ContentStatesEnum derivation, and never the /import page. This matches
          // the click→API gate — DONE enters the editor (clip generation is deliberately excluded
          // from the gate), anything else surfaces in the shell popup and returns home.
          // Backward compatibility: uploads created before `upload_editor_status` existed have no
          // value (the API omits the key → `undefined`), so fall through to the legacy derivation
          // below instead of gating on it. Truthy check catches both `null` and `undefined`.
          if (isSimplifiedUploadEnabled() && mediaContent.upload_editor_status) {
            if (mediaContent.upload_editor_status === 'DONE') {
              removeContentUploadEntry(mediaContent.id);
              setLoading(false);
              setCanGenerateClips(true);
              setIsSimplifiedUploadReady(true);
            } else {
              document.dispatchEvent(
                new CustomEvent(READINESS_SHOW_STATUS_EVENT, {
                  detail: {
                    content_id: mediaContent.id,
                    project_id: mediaContent.project_id,
                    title: mediaContent.title,
                    upload_editor_status: mediaContent.upload_editor_status,
                    batch_status: mediaContent.batch_status
                  }
                })
              );
              navigate('/', { replace: true });
            }
            return;
          }

          // Legacy flow (flag off) — unchanged.
          if (contentDetails.contentState === ContentStatesEnum.ContentReadyForPreview) {
            setLoading(false);
            setCanGenerateClips(true);
          } else if (
            [ContentStatesEnum.Generating, ContentStatesEnum.Processing].includes(contentDetails.contentState)
          ) {
            setShouldNavigateToImportPage(true);
          } else {
            contentLoaded.current = false;
          }
        })
        .catch(err => {
          const isUserNotInOrgOrNotAdmin =
            err.statusCode === 404 && err.message === USER_NOT_ADMIN_OR_IN_ORG_ERROR_MESSAGE;
          const isAbortError = err.name === 'AbortError';

          if (!(isUserNotInOrgOrNotAdmin || isAbortError)) {
            logger.error('Failed to fetch uploaded content', err);
          }
          navigate('/');
        });
      return;
    }
  }, [areFeatureFlagsLoaded, broadcastId, initUploadedContent, eventId, navigate, logger]);

  useEffect(() => {
    if (shouldNavigateToImportPage && broadcastId && eventId) {
      navigate(`/import/${eventId}/${broadcastId}`, { replace: true });
      setShouldNavigateToImportPage(false);
    }
  }, [shouldNavigateToImportPage, navigate, eventId, broadcastId]);

  useEffect(() => {
    if (contentState !== ContentStatesEnum.ContentReadyForPreview && broadcastId && contentUploadList[broadcastId]) {
      setLoading(false);
      return;
    }
    if (
      contentState === ContentStatesEnum.ContentReadyForPreview &&
      broadcastId &&
      contentUploadList[broadcastId] &&
      !contentLoaded.current
    ) {
      contentLoaded.current = true;
      setLoading(true);
      setCanGenerateClips(true);
      initContent(broadcastId).then(() => {
        setLoading(false);
      });
    }
  }, [broadcastId, eventId, contentState, contentUploadList, setLoading, loading]);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadAllClips, refetchVideoClips);
    return () => {
      EventBus.off(CustomEvents.ReloadAllClips, eventListener);
    };
  });

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadHighlightReels, refetchHighlightReels);
    return () => {
      EventBus.off(CustomEvents.ReloadHighlightReels, eventListener);
    };
  }, [refetchHighlightReels]);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadTranscript, loadData);
    return () => {
      EventBus.off(CustomEvents.ReloadTranscript, eventListener);
    };
  });

  useEffect(() => {
    clearAllClips();
  }, []);

  useEffect(() => {
    let jsConfetti;
    if (broadcastId && contentUploadList[broadcastId] && showSessionDetailsWithContext && isAllClipsLoaded) {
      const { showConfetti } = contentUploadList[broadcastId];

      if (showConfetti) {
        jsConfetti = new JSConfetti();
        jsConfetti.addConfetti().then(() => {
          handleConfettiState({ showConfetti: false, contentId: broadcastId });
        });
      }
    }
    return () => {
      jsConfetti?.clearCanvas();
    };
  }, [contentUploadList, showSessionDetailsWithContext, isAllClipsLoaded, broadcastId]);

  function loadData() {
    setLoading(true);
    initContent(broadcastId as string)
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        logger.error('[SessionDetail] Failed to initialize session content', {
          contentId: broadcastId,
          ...getErrorDiagnostics(err)
        });
        navigate(`/error/${err?.statusCode}`, { replace: true });
        setLoading(false);
      });
  }

  const handleTranscriptError = (err: any) => {
    navigate(`/error/${err?.statusCode}`, { replace: true });
  };

  const nav = useMemo(() => {
    const navItems = getNavItems({ broadcastId: broadcastId!, eventId: eventId!, useTextGrouping });

    // Covers both shapes of the text nav: the grouped single Text item, and the separate
    // Social/Blog/Email items that replace it when Use_CL_Text_Assets_Grouping is off.
    const AI_NAV_LABELS = new Set([
      NavigationEndpointConfig[NavigationEndpoint.takeaways].label,
      NavigationEndpointConfig[NavigationEndpoint.custom].label,
      NavigationEndpointConfig[NavigationEndpoint.text].label,
      NavigationEndpointConfig[NavigationEndpoint.social].label,
      NavigationEndpointConfig[NavigationEndpoint.blog].label,
      NavigationEndpointConfig[NavigationEndpoint.email].label,
      NavigationEndpointConfig[NavigationEndpoint.highlights].label
    ]);

    return navItems
      .filter(item => {
        if (!hasAIAddon && AI_NAV_LABELS.has(item.label)) {
          return false;
        }
        // Hide the Reels nav button when the highlight-reel feature flag is off.
        if (item.label === NavigationEndpointConfig[NavigationEndpoint.highlights].label) {
          return highlightReelEnabled;
        }
        return true;
      })
      .map((item: NavItemType) => {
        if (item.label === NavigationEndpointConfig[NavigationEndpoint.clips].label) {
          return {
            ...item,
            unseenCount: unseenClipsStore.length,
            onClick: e => {
              e.preventDefault();
              EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: true });
            }
          };
        }
        if (item.label === NavigationEndpointConfig[NavigationEndpoint.highlights].label) {
          return {
            ...item,
            showBetaTag: true,
            onClick: e => {
              e.preventDefault();
              EventBus.dispatch(CustomEvents.HighlightListPopUp, { isOpen: true });
            }
          };
        }
        return item;
      });
  }, [broadcastId, eventId, unseenClipsStore.length, useTextGrouping, highlightReelEnabled, hasAIAddon]);

  return (
    <>
      {showSessionDetailsWithContext ? (
        <TranscriptContextProvider
          parentMountTime={sessionDetailMountTime}
          broadcastId={broadcastId}
          eventId={eventId}
          onErrorCallback={handleTranscriptError}
          parentContext="SessionDetail"
        >
          <PostAutoGenerateProvider>
            <SessionDetailLayout isLoading={loading} navMenu={nav} />
          </PostAutoGenerateProvider>
        </TranscriptContextProvider>
      ) : (
        <SessionDetailLayout isLoading={loading} navMenu={nav} />
      )}
      <RecommendChromiumBrowserPopup />
      {coreStore?.content &&
        coreStore?.content?.id === broadcastId &&
        coreStore?.content?.project_id === eventId &&
        showSessionDetailsWithContext && <EventMigrationPopup content={coreStore.content} />}
    </>
  );
}

export default SessionDetail;
